import type { LensCard, LensCardData, LensCardHeight, LensCardSize, LensSection } from '~/types/leLens';

const UNGROUPED = '__ungrouped';

export type LensCardFrame = { size: LensCardSize; height: LensCardHeight };

/**
 * A smart starting frame reflects the analytic job and the amount of result
 * the reader has to parse. It is applied only when an editor explicitly
 * chooses Smart arrange; an edited card keeps its saved frame until then.
 */
export function smartCardFrame(
  card: Pick<LensCard, 'type'> & Partial<Pick<LensCard, 'title'>>,
  data?: LensCardData,
): LensCardFrame {
  const rowCount = data?.rows?.length ?? 0;
  const seriesCount = Math.max(0, (data?.columns?.length ?? 0) - 1);

  switch (card.type) {
    case 'metric':
      // A partial answer carries a visible explanation and warning. Giving it
      // a compact tile is how the original frame could collide with its row.
      if (data?.truncated) {
        return { size: 'large', height: 'tall' };
      }
      return { size: 'small', height: 'short' };
    case 'table':
      return rowCount > 12 ? { size: 'full', height: 'xtall' } : { size: 'large', height: 'tall' };
    case 'insight':
      return { size: 'large', height: 'standard' };
    case 'pie':
      return { size: 'medium', height: 'standard' };
    case 'heatmap':
      // Two category axes both need room: a narrow heatmap collapses its cells
      // to slivers long before a bar chart of the same data stops being legible.
      return { size: 'full', height: 'tall' };
    case 'boxplot':
      return rowCount > 8 ? { size: 'full', height: 'tall' } : { size: 'large', height: 'tall' };
    default:
      // Legends with many series need width to stay readable. A wide line
      // chart with eight series is a trend-comparison surface, not a sidebar.
      if (seriesCount >= 5) {
        return { size: 'full', height: 'tall' };
      }
      return { size: 'large', height: 'tall' };
  }
}

function groupId(card: LensCard, sectionIds: Set<string>): string {
  return card.section && sectionIds.has(card.section) ? card.section : UNGROUPED;
}

/**
 * Moves a card to the frame occupied by another card. Crossing a section is
 * intentional: the card joins the destination section and the saved spec is
 * rebuilt in the exact order the canvas renders. No coordinates are stored,
 * which keeps the same arrangement responsive at every breakpoint.
 */
export function moveLensCard(
  cards: Array<LensCard>,
  sections: Array<LensSection>,
  activeId: string,
  overId: string,
): Array<LensCard> {
  if (activeId === overId) {
    return cards;
  }
  const activeCard = cards.find((card) => card.id === activeId);
  const overCard = cards.find((card) => card.id === overId);
  if (!activeCard || !overCard) {
    return cards;
  }

  const sectionIds = new Set(sections.map((section) => section.id));
  const order = [UNGROUPED, ...sections.map((section) => section.id)];
  const groups = new Map(order.map((id) => [id, [] as Array<LensCard>]));
  cards.forEach((card) => groups.get(groupId(card, sectionIds))?.push(card));

  const source = groupId(activeCard, sectionIds);
  const destination = groupId(overCard, sectionIds);
  const sourceCards = groups.get(source) ?? [];
  groups.set(
    source,
    sourceCards.filter((card) => card.id !== activeId),
  );

  const destinationCards = groups.get(destination) ?? [];
  const insertAt = destinationCards.findIndex((card) => card.id === overId);
  const moved = { ...activeCard, section: destination === UNGROUPED ? undefined : destination };
  destinationCards.splice(insertAt < 0 ? destinationCards.length : insertAt, 0, moved);
  groups.set(destination, destinationCards);

  return order.flatMap((id) => groups.get(id) ?? []);
}

/**
 * Moves a card to an explicitly chosen section, including one that is empty.
 * The canvas grids are intentionally isolated so their local compaction does
 * not push cards in another section around; this action supplies the missing
 * cross-section movement without pretending a drag can cross that boundary.
 */
export function moveLensCardToSection(
  cards: Array<LensCard>,
  sections: Array<LensSection>,
  activeId: string,
  destinationSectionId?: string,
): Array<LensCard> {
  const activeCard = cards.find((card) => card.id === activeId);
  const sectionIds = new Set(sections.map((section) => section.id));
  const destination = destinationSectionId && sectionIds.has(destinationSectionId) ? destinationSectionId : UNGROUPED;
  if (!activeCard || groupId(activeCard, sectionIds) === destination) {
    return cards;
  }

  const order = [UNGROUPED, ...sections.map((section) => section.id)];
  const groups = new Map(order.map((id) => [id, [] as Array<LensCard>]));
  cards.forEach((card) => groups.get(groupId(card, sectionIds))?.push(card));
  const source = groupId(activeCard, sectionIds);
  groups.set(
    source,
    (groups.get(source) ?? []).filter((card) => card.id !== activeId),
  );

  // Position belongs to a canvas, not the card's analytical definition. The
  // destination canvas gives it its first vacant frame rather than carrying a
  // coordinate that was only meaningful in the source section.
  (groups.get(destination) ?? []).push({
    ...activeCard,
    layout: undefined,
    section: destination === UNGROUPED ? undefined : destination,
  });
  return order.flatMap((id) => groups.get(id) ?? []);
}
